import { useState, useCallback, useEffect } from 'react'
import Editor from '@monaco-editor/react'
import { FileCode, Copy, Download, Check, Wand2, Minimize2, RotateCcw } from 'lucide-react'
import { Button } from './button'
import { Badge } from './badge'

// Format JSON with different indentation levels
const formatJSON = (content: string, indent: number = 2): string => {
  try {
    const parsed = JSON.parse(content)
    return JSON.stringify(parsed, null, indent)
  } catch (error) {
    return content // Return original if parsing fails
  }
}

// Minify JSON (remove all whitespace)
const minifyJSON = (content: string): string => {
  try {
    const parsed = JSON.parse(content)
    return JSON.stringify(parsed)
  } catch (error) {
    return content
  }
}


// Get JSON statistics
const getJSONStats = (content: string) => {
  try {
    const parsed = JSON.parse(content)
    
    const countProperties = (obj: any): number => {
      if (Array.isArray(obj)) {
        return obj.length
      } else if (obj && typeof obj === 'object') {
        return Object.keys(obj).length
      }
      return 0
    }
    
    const countDepth = (obj: any, depth = 0): number => {
      if (Array.isArray(obj)) {
        return Math.max(depth, ...obj.map(item => countDepth(item, depth + 1)))
      } else if (obj && typeof obj === 'object') {
        const depths = Object.values(obj).map(val => countDepth(val, depth + 1))
        return depths.length ? Math.max(depth, ...depths) : depth
      }
      return depth
    }

    return {
      valid: true,
      properties: countProperties(parsed),
      depth: countDepth(parsed),
      type: Array.isArray(parsed) ? 'array' : typeof parsed
    }
  } catch {
    return {
      valid: false,
      properties: 0,
      depth: 0,
      type: 'invalid'
    }
  }
}

interface JSONViewerProps {
  content: string
  filename: string
  onDownload?: () => void
  onContentChange?: (newContent: string) => void
  className?: string
  showHeader?: boolean
  defaultFormatted?: boolean
}

export function JSONViewer({ 
  content, 
  filename, 
  onDownload,
  onContentChange,
  className = '',
  showHeader = true,
  defaultFormatted = true
}: JSONViewerProps) {
  const [copied, setCopied] = useState(false)
  const [theme, setTheme] = useState<'vs-dark' | 'light'>('light')
  const [displayContent, setDisplayContent] = useState(content)
  const [isFormatted, setIsFormatted] = useState(false)
  const [indentSize, setIndentSize] = useState(2)

  const jsonStats = getJSONStats(content)
  const isValid = jsonStats.valid
  const lineCount = displayContent.split('\n').length
  const fileSize = new Blob([displayContent]).size

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(displayContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy JSON:', err)
    }
  }, [displayContent])

  const handleFormat = useCallback(() => {
    if (isValid) {
      const formatted = formatJSON(content, indentSize)
      setDisplayContent(formatted)
      setIsFormatted(true)
      onContentChange?.(formatted)
    }
  }, [content, indentSize, isValid, onContentChange])

  const handleMinify = useCallback(() => {
    if (isValid) {
      const minified = minifyJSON(content)
      setDisplayContent(minified)
      setIsFormatted(false)
      onContentChange?.(minified)
    }
  }, [content, isValid, onContentChange])

  const handleReset = useCallback(() => {
    setDisplayContent(content)
    setIsFormatted(false)
  }, [content])

  const toggleTheme = () => {
    setTheme(prev => prev === 'vs-dark' ? 'light' : 'vs-dark')
  }

  // Auto-format on mount if requested and content is valid JSON
  useEffect(() => {
    if (defaultFormatted && isValid && !isFormatted) {
      const formatted = formatJSON(content, indentSize)
      setDisplayContent(formatted)
      setIsFormatted(true)
    }
  }, [content, defaultFormatted, isValid, isFormatted, indentSize])

  // Reset display content when original content changes
  useEffect(() => {
    setDisplayContent(content)
    setIsFormatted(false)
  }, [content])

  // Render action buttons
  const ActionButtons = () => (
    <div className="flex gap-1 flex-shrink-0">
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={toggleTheme}
        className="h-7 px-2"
        title={`Switch to ${theme === 'vs-dark' ? 'light' : 'dark'} theme`}
      >
        {theme === 'vs-dark' ? '☀️' : '🌙'}
      </Button>
      
      {isValid && (
        <>
          <select
            value={indentSize}
            onChange={(e) => setIndentSize(Number(e.target.value))}
            className="h-7 px-2 text-xs border rounded"
            title="Indentation size"
          >
            <option value={2}>2 spaces</option>
            <option value={4}>4 spaces</option>
            <option value={8}>8 spaces</option>
          </select>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleFormat}
            className="h-7 px-2"
            title="Format JSON"
            disabled={!isValid}
          >
            <Wand2 className="h-3 w-3" />
          </Button>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleMinify}
            className="h-7 px-2"
            title="Minify JSON"
            disabled={!isValid}
          >
            <Minimize2 className="h-3 w-3" />
          </Button>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleReset}
            className="h-7 px-2"
            title="Reset to original"
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
        </>
      )}
      
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={handleCopy}
        className="h-7 px-2"
        title="Copy JSON"
      >
        {copied ? (
          <Check className="h-3 w-3 text-green-500" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </Button>
      
      {onDownload && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onDownload}
          className="h-7 px-2"
          title="Download file"
        >
          <Download className="h-3 w-3" />
        </Button>
      )}
    </div>
  )

  return (
    <div className={`h-full flex flex-col ${className}`}>
      {/* Header with file info and actions */}
      {showHeader && (
        <div className="flex items-center justify-between p-3 border-b bg-white">
          <div className="flex items-center gap-2 min-w-0">
            <FileCode className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="font-medium text-sm truncate">{filename}</span>
            <Badge variant={isValid ? "default" : "destructive"} className="text-xs">
              {isValid ? 'VALID JSON' : 'INVALID JSON'}
            </Badge>
            {isValid && (
              <Badge variant="secondary" className="text-xs">
                {jsonStats.type.toUpperCase()}
              </Badge>
            )}
          </div>
          
          <ActionButtons />
        </div>
      )}

      {/* Monaco Editor */}
      <div className="flex-1 relative overflow-hidden bg-gray-50 border">
        <Editor
          language="json"
          value={displayContent}
          theme={theme}
          options={{
            readOnly: true,
            minimap: { 
              enabled: lineCount > 50
            },
            scrollBeyondLastLine: false,
            fontSize: 12,
            fontFamily: 'JetBrains Mono, Monaco, Consolas, "Courier New", monospace',
            lineNumbers: 'on',
            folding: true,
            wordWrap: 'on',
            automaticLayout: true,
            padding: { top: 16, bottom: 16 },
            renderWhitespace: 'selection',
            bracketPairColorization: { enabled: true },
            smoothScrolling: true,
            cursorBlinking: 'smooth',
            selectOnLineNumbers: true,
            lineNumbersMinChars: 3,
            glyphMargin: false,
            contextmenu: false,
            quickSuggestions: false,
            parameterHints: { enabled: false },
            suggestOnTriggerCharacters: false,
            acceptSuggestionOnEnter: 'off',
            tabCompletion: 'off',
            hover: { enabled: true },
            links: false,
            // JSON-specific options
            formatOnType: true,
            formatOnPaste: true
          }}
          loading={
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <FileCode className="h-8 w-8 mx-auto mb-2 animate-pulse" />
                <p className="text-sm">Loading JSON viewer...</p>
              </div>
            </div>
          }
        />
      </div>

      {/* Enhanced status bar for JSON */}
      <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-600">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span>{lineCount.toLocaleString()} lines • {formatFileSize(fileSize)}</span>
            {isValid && (
              <>
                <span>•</span>
                <span>{jsonStats.properties} {jsonStats.type === 'array' ? 'items' : 'properties'}</span>
                <span>•</span>
                <span>Depth: {jsonStats.depth}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span>JSON</span>
            <span>•</span>
            <span>UTF-8</span>
            <span>•</span>
            <span className="text-gray-500">Read-only</span>
            {isFormatted && (
              <>
                <span>•</span>
                <span className="text-primary">Formatted</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}